import React from "react";

type Localization = {
  localeString: string; // BCP 47 language tag, e.g "de-AT"
  timezone: string; // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
  titlePerformanceCalculator: string;
  titleHistoric: string;
  dropdownChooseMainFond: string;
  dropdownChooseAdditionalFonds: string;
  checkboxAga: string;
  labelIndexed: string;
  labelInitial: string;
  labelMonthly: string;
  labelDatepicker: string;
  chartTitlePerformance: string;
  chartTitleInvestition: string;
  warningAdjusted: (fund: string, dateFrom: string, dateTo: string) => React.ReactNode;
  warningNotAvailableLifetime: (fund: string, dateFrom: string, dateTo: string) => React.ReactNode;
  infoMaximumFonds: string;
  infoRuntime: string;
  disclaimerIndexed: string;
  disclaimerCalculation: string;
  disclaimerOnlineOrder: string;
  disclaimerTitle: string;
  headerFonds: string;
  headerEndResult: string;
  headerEndResultInclusive: string;
  headerPerformance: string;
  headerOnlineOrder: string;
  ctaButtonBuy: string;
  dropdownPlaceholder: string;
  currencySign: string;
  currency: string;
  depositChartLabel: string;
  toString: string;
  resetButtonLabel: string;
  datePickerStart: string;
  datePickerEnd: string;
  datePickerButtonAriaLabel?: string;
  datePickerSwitchViewButtonAriaLabel?: string;
  datePickerDayViewSwitchingButtonText?: string;
  datePickerMonthViewSwitchingButtonText?: string;
  datePickerYearViewSwitchingButtonText?: string;
};

const localization: Localization[] = [
  {
    localeString: "de-AT", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance-Rechner",
    titleHistoric: "Darstellung der historischen Wertentwicklung",
    dropdownChooseMainFond: "Wählen Sie einen Fonds aus",
    dropdownChooseAdditionalFonds: "Wählen Sie bis zu vier Vergleichsfonds aus",
    checkboxAga: "Ausgabe-Aufschlag einbeziehen",
    labelIndexed: "Indexiert [1]",
    labelInitial: "Einmalerlag",
    labelMonthly: "Monatliche Einzahlung",
    labelDatepicker: "Veranlagungszeitraum",
    chartTitlePerformance: "Wertentwicklung",
    chartTitleInvestition: " Investition gesamt:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Der Veranlagungszeitraum wurde angepasst, da die Daten für ",
      <strong key={1}>{fund}</strong>,
      " von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " verfügbar sind.",
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Der Fonds ",
      <strong key={1}>{fund}</strong>,
      " kann in der Berechnung nicht berücksichtigt werden, da dessen Laufzeit von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " beträgt. Bitte passen Sie den Zeitraum an, oder wählen Sie andere Vergleichfonds aus.",
    ],
    infoMaximumFonds: "Maximale Anzahl an Vergleichsfonds wurde ausgewählt.",
    infoRuntime: "Laufzeit kürzer als 10 Jahre.",
    disclaimerIndexed:
      "[1] Jährliche Anpassung der monatlichen Einzahlung (z.B. 2 % – entspricht dem langfristigen Inflationsziel" +
      " der Europäischen Zentralbank)",
    disclaimerCalculation:
      "[2] Die Berechnung der Wertentwicklung erfolgt lt. OeKB Methode. Die Wertentwicklung unterstellt eine" +
      " vollständige Wiederveranlagung der Ausschüttung und berücksichtigt die Verwaltungsgebühr sowie eine" +
      " allfällige erfolgsbezogene Vergütung. Der bei Kauf gegebenenfalls einmalige Ausgabeaufschlag ist in der" +
      ' Darstellung nicht enthalten, außer Sie setzen das "Häkchen" bei "Ausgabeaufschlag berücksichtigen".' +
      "  Allenfalls individuelle transaktionsbezogene oder laufend ertragsmindernde Kosten (z.B. Konto- und Depotgebühren) sowie Steuern sind in der Darstellung nicht berücksichtigt. Die Wertentwicklung der Vergangenheit lässt keine verlässlichen Rückschlüsse auf die zukünftige Entwicklung eines Fonds zu.",
    disclaimerOnlineOrder:
      "[3] Wenn Sie auf Kaufen klicken, werden Sie zu George, dem modernsten Banking Österreichs, weitergeleitet.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fonds",
    headerEndResult: "Endbetrag",
    headerEndResultInclusive: "Ausgabeaufschlag berücksichtigt",
    headerPerformance: "Wertentwicklung gesamt [2]",
    headerOnlineOrder: "Online bei Erste Bank und Sparkassen kaufen [3]",
    ctaButtonBuy: "Kaufen",
    dropdownPlaceholder: "Fonds suchen",
    currencySign: "€",
    currency: "EUR",
    depositChartLabel: "Gesamteinzahlung",
    toString: "bis",
    resetButtonLabel: "Zurücksetzen",
    datePickerStart: "Startdatum",
    datePickerEnd: "Enddatum",
    datePickerButtonAriaLabel:
      "Öffnen Sie den Kalender für die Auswahl des Datums, das ausgewählt Datum ist der",
    datePickerSwitchViewButtonAriaLabel: "Wechseln Sie die Ansicht des Kalenders",
    datePickerDayViewSwitchingButtonText: "Tag-Ansicht",
    datePickerMonthViewSwitchingButtonText: "Monats-Ansicht",
    datePickerYearViewSwitchingButtonText: "Jahres-Ansicht",
  },
  {
    localeString: "en-AT", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Performance",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",
      <strong key={1}>{fund}</strong>,
      " cannot be included in the calculation because its maturity is from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
      ". Please adjust the period or select other comparison funds.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime: "Term shorter than 10 years.",
    disclaimerIndexed:
      "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      "[2] The performance is calculated according to the OeKB method. The performance assumes full reinvestment of" +
      " the distribution and takes into account the management fee and any performance-related remuneration. The" +
      " one-time front-end load, if any, at the time of purchase is not included in the presentation, unless you" +
      ' check the "Take front-end load into account" box. Any individual transaction-related or ongoing' +
      " income-reducing costs (e.g. account and custody fees) and taxes are not included in the presentation. Past performance is not a reliable indicator of a fund's future performance.",
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to George Online Banking.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currency: "EUR",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "Start date",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
  {
    localeString: "de-DE", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance-Rechner",
    titleHistoric: "Darstellung der historischen Wertentwicklung",
    dropdownChooseMainFond: "Wählen Sie einen Fonds aus",
    dropdownChooseAdditionalFonds: "Wählen Sie bis zu vier Vergleichsfonds aus",
    checkboxAga: "Ausgabe-Aufschlag einbeziehen",
    labelIndexed: "Indexiert [1]",
    labelInitial: "Einmalerlag",
    labelMonthly: "Monatliche Einzahlung",
    labelDatepicker: "Veranlagungszeitraum",
    chartTitlePerformance: "Wertentwicklung",
    chartTitleInvestition: " Investition gesamt:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Der Veranlagungszeitraum wurde angepasst, da die Daten für ",
      <strong key={1}>{fund}</strong>,
      " von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " verfügbar sind.",
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Der Fonds ",
      <strong key={1}>{fund}</strong>,
      " kann in der Berechnung nicht berücksichtigt werden, da dessen Laufzeit von ",
      <strong key={2}>{dateFrom}</strong>,
      " bis ",
      <strong key={3}>{dateTo}</strong>,
      " beträgt. Bitte passen Sie den Zeitraum an, oder wählen Sie andere Vergleichfonds aus.",
    ],
    infoMaximumFonds: "Maximale Anzahl an Vergleichsfonds wurde ausgewählt.",
    infoRuntime: "Laufzeit kürzer als 10 Jahre.",
    disclaimerIndexed:
      "[1] Jährliche Anpassung der monatlichen Einzahlung (z.B. 2 % – entspricht dem langfristigen Inflationsziel" +
      " der Europäischen Zentralbank)",
    disclaimerCalculation:
      '[2] Die Berechnung der Wertentwicklung erfolgt lt. OeKB Methode. Die Wertentwicklung unterstellt eine vollständige Wiederveranlagung der Ausschüttung und berücksichtigt die Verwaltungsgebühr sowie eine allfällige erfolgsbezogene Vergütung. Der bei Kauf gegebenenfalls einmalige Ausgabeaufschlag ist in der Darstellung nicht enthalten, außer Sie setzen das "Häkchen" bei "Ausgabeaufschlag berücksichtigen".  Allenfalls individuelle transaktionsbezogene oder laufend ertragsmindernde Kosten (z.B. Konto- und Depotgebühren) sowie Steuern sind in der Darstellung nicht berücksichtigt. Die Wertentwicklung der Vergangenheit lässt keine verlässlichen Rückschlüsse auf die zukünftige Entwicklung eines Fonds zu.',
    disclaimerOnlineOrder:
      "[3] Wenn Sie auf Kaufen klicken, werden Sie zu George, dem modernsten Banking Österreichs, weitergeleitet.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fonds",
    headerEndResult: "Endbetrag",
    headerEndResultInclusive: "Ausgabeaufschlag berücksichtigt",
    headerPerformance: "Wertentwicklung gesamt [2]",
    headerOnlineOrder: "Online bei Erste Bank und Sparkassen kaufen [3]",
    ctaButtonBuy: "Kaufen",
    dropdownPlaceholder: "Fonds suchen",
    currencySign: "€",
    currency: "EUR",
    depositChartLabel: "Gesamteinzahlung",
    toString: "bis",
    resetButtonLabel: "Zurücksetzen",
    datePickerStart: "Startdatum",
    datePickerEnd: "Enddatum",
    datePickerButtonAriaLabel:
      "Öffnen Sie den Kalender für die Auswahl des Datums, das ausgewählt Datum ist der",
    datePickerSwitchViewButtonAriaLabel: "Wechseln Sie die Ansicht des Kalenders",
    datePickerDayViewSwitchingButtonText: "Tag-Ansicht",
    datePickerMonthViewSwitchingButtonText: "Monats-Ansicht",
    datePickerYearViewSwitchingButtonText: "Jahres-Ansicht",
  },
  {
    localeString: "en-DE", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Performance",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",
      <strong key={1}>{fund}</strong>,
      " cannot be included in the calculation because its maturity is from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
      ". Please adjust the period or select other comparison funds.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime: "Term shorter than 10 years.",
    disclaimerIndexed:
      "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      '[2] The performance is calculated according to the OeKB method. The performance assumes full reinvestment of the distribution and takes into account the management fee and any performance-related remuneration. The one-time front-end load, if any, at the time of purchase is not included in the presentation, unless you check the "Take front-end load into account" box. Any individual transaction-related or ongoing income-reducing costs (e.g. account and custody fees) and taxes are not included in the presentation. Past performance is not a reliable indicator of a fund\'s future performance.',
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to George Online Banking.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currency: "EUR",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "Start date",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
  {
    localeString: "sk-SK", // BCP 47 language tag, e.g "de-AT"
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Kalkulačka výkonnosti",
    titleHistoric: "Zobrazenie historickej výkonnosti",
    dropdownChooseMainFond: "Vyberte si fond",
    dropdownChooseAdditionalFonds: "Vyberte fondy na porovnanie (max. 4)",
    checkboxAga: "Zohľadniť vstupný poplatok",
    labelIndexed: "Indexované [1]",
    labelInitial: "Počiatočná investícia",
    labelMonthly: "Mesačná investícia",
    labelDatepicker: "Investičné obdobie",
    chartTitlePerformance: "Hodnota investície",
    chartTitleInvestition: "Celková investícia:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "Investičné obdobie bolo upravené, pretože dáta pre ",
      <strong key={1}>{fund}</strong>,
      " sú k dispozícii iba od ",
      <strong key={2}>{dateFrom}</strong>,
      " do ",
      <strong key={3}>{dateTo}</strong>,
      ".",
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "Fond ",
      <strong key={1}>{fund}</strong>,
      " nemôže byť do výpočtu zaradený, pretože dáta sú k dispozícii iba od dátumu vytvorenia fondu ",
      <strong key={2}>{dateFrom}</strong>,
      " .Prosím upravte investičné obdobie alebo vyberte iné fondy na porovnanie.",
    ],
    infoMaximumFonds: "Maximálny počet vybraných fondov.",
    infoRuntime:
      "Zvolené investičné obdobie je dlhšie ako doba existencie fondu. Prosím upravte investičné obdobie.",
    disclaimerIndexed: "[1] Každoročné navýšenie výšky mesačnej investície o zadané percento.",
    disclaimerCalculation:
      "[2] Prezentovaná výkonnosť za zvolené investičné obdobie (časovo vážená miera návratnosti) zahŕňa poplatky ktoré sú účtované z majetku fondu. Akékoľvek iné poplatky, ktoré by mohli byť uplatnené pri nákupe, alebo predaji, prípadne ďalšie špecifické náklady, nie sú započítané. Hodnota investície sa môže aj znižovať a nie je zaručená návratnosť pôvodne investovanej sumy. Výnosy z investície do podielových fondov dosiahnuté v minulosti nie sú zárukou budúcich výnosov. ",
    disclaimerOnlineOrder:
      "[3] Ak kliknete na Kúpiť, budete presmerovaný do internetbankingu George.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fond",
    headerEndResult: "Konečná suma",
    headerEndResultInclusive: "Zohľadnený vstupný poplatok",
    headerPerformance: "Celková výkonnosť [2]",
    headerOnlineOrder: "Nakúpiť online v Slovenskej sporiteľni [3]",
    ctaButtonBuy: "Kúpiť",
    dropdownPlaceholder: "Vyhľadať fondy",
    currencySign: "€",
    currency: "EUR",
    depositChartLabel: "Celková investícia",
    toString: "do",
    resetButtonLabel: "VYMAZAŤ NASTAVENIA",
    datePickerStart: "Dátum začiatku",
    datePickerEnd: "Dátum ukončenia",
    datePickerButtonAriaLabel: "Otvorte kalendár pre výber dátumu, vybraný dátum je",
    datePickerSwitchViewButtonAriaLabel: "Prepnúť zobrazenie kalendára",
    datePickerDayViewSwitchingButtonText: "Zobrazenie dňa",
    datePickerMonthViewSwitchingButtonText: "Zobrazenie mesiaca",
    datePickerYearViewSwitchingButtonText: "Zobrazenie roka",
  },
  {
    localeString: "en-SK", // BCP 47 language tag
    timezone: "Europe/Vienna", // Luxon Timezone string, e.g. "Europe/Vienna" https://moment.github.io/luxon/#/tour?id=time-zones
    titlePerformanceCalculator: "Performance calculator",
    titleHistoric: "Display the historical performance",
    dropdownChooseMainFond: "Please select a fund",
    dropdownChooseAdditionalFonds: "Please select up to four funds for comparison",
    checkboxAga: "Take upfront-fee into account",
    labelIndexed: "Indexed [1]",
    labelInitial: "Initial investment",
    labelMonthly: "Monthly investment",
    labelDatepicker: "Investment period",
    chartTitlePerformance: "Investment Value",
    chartTitleInvestition: "Total investment:",
    warningAdjusted: (fund, dateFrom, dateTo) => [
      "The investment period has been adjusted as the data for ",
      <strong key={1}>{fund}</strong>,
      " is available from ",
      <strong key={2}>{dateFrom}</strong>,
      " to ",
      <strong key={3}>{dateTo}</strong>,
    ],
    warningNotAvailableLifetime: (fund, dateFrom, dateTo) => [
      "The fund ",
      <strong key={1}>{fund}</strong>,
      " cannot be included in the calculation because data are available only from the fund launch date ",
      <strong key={2}>{dateFrom}</strong>,
      ". Please adjust investment period or select other funds for comparison.",
    ],
    infoMaximumFonds: "Maximum number of funds selected.",
    infoRuntime:
      "Selected investment period is longer than life period of the fund. Please adjust the investment period.",
    disclaimerIndexed:
      "[1] Annual adjustment of the monthly investment (e.g., 2% – corresponds to the European Central Bank’s long-term inflation target)",
    disclaimerCalculation:
      "[2] Presented performance (time-weighted rate of return) for selected investment period includes fees paid by the fund. Any other fees, that might be applicable on purchase, any individual transaction specific costs or ongoing costs or costs imposed by the credit institution maintaining the clients custody account are not considered. Past performance is neither a reliable indicator nor a guarantee for the future performance of a fund.",
    disclaimerOnlineOrder:
      "[3] If you click on Buy you will be redirected to internet banking George.",
    disclaimerTitle: "Warnhinweise gemäß InvFG 2011",
    headerFonds: "Fund",
    headerEndResult: "Final amount",
    headerEndResultInclusive: "Upfront-fee taken into account",
    headerPerformance: "Total performance [2]",
    headerOnlineOrder: "Buy online at Erste Bank and Sparkassen [3]",
    ctaButtonBuy: "Buy",
    dropdownPlaceholder: "Search funds",
    currencySign: "€",
    currency: "EUR",
    depositChartLabel: "Total investment",
    toString: "to",
    resetButtonLabel: "Reset",
    datePickerStart: "Start date",
    datePickerEnd: "End date",
    datePickerButtonAriaLabel: "Open the calendar to select the date, the selected date is",
    datePickerSwitchViewButtonAriaLabel: "Switch the calendar view",
    datePickerDayViewSwitchingButtonText: "Day view",
    datePickerMonthViewSwitchingButtonText: "Month view",
    datePickerYearViewSwitchingButtonText: "Year view",
  },
];

export default localization;
